import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, tap, catchError } from 'rxjs/operators';
import { User, UserStatus } from '../../shared/models';
import { ApiService } from './api.service';
import { Router } from '@angular/router';
import { TokenStorageService, UserSession } from './token-storage.service';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  expiresAt: string;
  mustChangePassword: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);

  constructor() {
    this.checkStoredAuth();
  }

  private checkStoredAuth(): void {
    if (!this.tokenStorage.isValidToken()) {
      return;
    }

    const user = this.tokenStorage.getUser() as unknown as User;

    if (user) {
      // Check if user is inactive/deleted - redirect to frozen account but keep them authenticated
      if (user['status'] === UserStatus.Inactive || user['status'] === UserStatus.Deleted) {
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);

        // Redirect to frozen account page
        this.router.navigate(['/frozen-account']);
        return;
      }

      this.currentUserSubject.next(user);
      this.isAuthenticatedSubject.next(true);
    }
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.api.post<LoginResponse>('/Auth/login', credentials, { withCredentials: true, skipToast: true }).pipe(
      tap(response => {
        // Store tokens securely
        this.tokenStorage.setToken(response.accessToken);
        this.tokenStorage.setTokenExpiry(response.expiresAt);
        this.tokenStorage.setUser(response.user as unknown as UserSession);

        // Update subjects
        this.currentUserSubject.next(response.user);
        this.isAuthenticatedSubject.next(true);

        // Check if user is inactive or deleted
        if (response.user.status === UserStatus.Inactive || response.user.status === UserStatus.Deleted) {
          // Redirect to frozen account page
          this.router.navigate(['/frozen-account']);
        }
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<void> {
    return this.api.post<void>('/Auth/logout', {}, { withCredentials: true }).pipe(
      tap(() => {
        this.clearSession();
      }),
      catchError(() => {
        // Even if logout fails on server, clear session storage
        this.clearSession();
        return of(void 0);
      })
    );
  }

  private clearSession(): void {
    this.tokenStorage.clear();
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  refreshCurrentUser(): Observable<User> {
    return this.api.get<User>('/Auth/me', { skipToast: true }).pipe(
      tap(user => {
        // Check if user became deleted
        if (user.status === UserStatus.Deleted) {
          this.logout().subscribe(() => {
            this.router.navigate(['/login']);
          });
          throw new Error('User account has been deleted');
        }

        this.tokenStorage.setUser(user as unknown as UserSession);
        this.currentUserSubject.next(user);
      })
    );
  }

  // Refresh access token using refresh token from HttpOnly cookie
  refreshToken(): Observable<LoginResponse> {
    return this.api.post<LoginResponse>('/Auth/refresh', {}, { withCredentials: true }).pipe(
      tap(response => {
        // Update tokens in memory
        this.tokenStorage.refreshToken(response.accessToken, response.expiresAt);
        this.tokenStorage.setUser(response.user as unknown as UserSession);
        this.currentUserSubject.next(response.user);
      }),
      catchError(error => {
        // If refresh fails, clear session and redirect to login
        this.clearSession();
        this.router.navigate(['/login']);
        return throwError(() => error);
      })
    );
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.role) {
      return false;
    }

    return user.role.permissions.includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  // Establish authenticated session (used after invite claim)
  // Minimal side effects: only updates storage and subjects
  establishSession(user: User, accessToken: string, expiresAt?: string): void {
    this.tokenStorage.setToken(accessToken);
    if (expiresAt) {
      this.tokenStorage.setTokenExpiry(expiresAt);
    }
    this.tokenStorage.setUser(user as unknown as UserSession);
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);
  }

}